import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, Btn, Modal, Field, Input, Select } from "@/components/ui-kit";
import { Plus, Trash2, Edit3, Users } from "lucide-react";

const empty = { name: "", client_type: "individual", mobile: "", email: "", gst_number: "", billing_address: "", credit_terms_days: 0 };

export default function Clients() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => setItems((await api.get("/owner/clients")).data);
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e?.preventDefault();
    const data = { ...editing, credit_terms_days: Number(editing.credit_terms_days || 0) };
    if (!data.email) delete data.email;
    if (data.id) await api.patch(`/owner/clients/${data.id}`, data);
    else await api.post("/owner/clients", data);
    setOpen(false); setEditing(null); load();
  };

  const del = async (id) => {
    if (!confirm("Delete client?")) return;
    await api.delete(`/owner/clients/${id}`); load();
  };

  return (
    <div data-testid="clients-page">
      <PageHeader title="Clients" subtitle="Individuals and corporate accounts with GSTIN and credit terms."
        actions={<Btn data-testid="client-add" onClick={() => { setEditing({...empty}); setOpen(true); }}><Plus className="h-4 w-4" /> Add Client</Btn>}
      />
      <div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
        <table className="tbl">
          <thead><tr><th>Name</th><th>Type</th><th>Mobile</th><th>Email</th><th>GSTIN</th><th>Credit Days</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-10 text-zinc-500"><Users className="h-8 w-8 mx-auto mb-2 text-zinc-300" /> No clients yet.</td></tr>
            ) : items.map((c) => (
              <tr key={c.id}>
                <td className="font-medium text-asphalt">{c.name}</td>
                <td><span className="badge badge-muted capitalize">{c.client_type}</span></td>
                <td>{c.mobile || "—"}</td>
                <td>{c.email || "—"}</td>
                <td className="font-mono text-xs">{c.gst_number || "—"}</td>
                <td>{c.credit_terms_days || 0}</td>
                <td className="text-right">
                  <Btn variant="ghost" onClick={() => { setEditing(c); setOpen(true); }} data-testid={`client-edit-${c.id}`}><Edit3 className="h-4 w-4" /></Btn>
                  <Btn variant="ghost" onClick={() => del(c.id)} data-testid={`client-delete-${c.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Btn>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing?.id ? "Edit Client" : "Add Client"} testid="client-modal"
        footer={<><Btn variant="outline" onClick={() => setOpen(false)}>Cancel</Btn><Btn onClick={submit} data-testid="client-save">Save</Btn></>}>
        {editing && (
          <form onSubmit={submit} className="grid grid-cols-2 gap-4">
            <Field label="Name" required><Input data-testid="cli-name" value={editing.name} onChange={(e) => setEditing({...editing, name: e.target.value})} required /></Field>
            <Field label="Type" required>
              <Select value={editing.client_type} onChange={(e) => setEditing({...editing, client_type: e.target.value})}>
                <option value="individual">Individual</option><option value="corporate">Corporate</option>
              </Select>
            </Field>
            <Field label="Mobile"><Input value={editing.mobile || ""} onChange={(e) => setEditing({...editing, mobile: e.target.value})} /></Field>
            <Field label="Email"><Input type="email" value={editing.email || ""} onChange={(e) => setEditing({...editing, email: e.target.value})} /></Field>
            {editing.client_type === "corporate" && (
              <>
                <Field label="GST Number"><Input value={editing.gst_number || ""} onChange={(e) => setEditing({...editing, gst_number: e.target.value.toUpperCase()})} /></Field>
                <Field label="Credit Days"><Input type="number" value={editing.credit_terms_days} onChange={(e) => setEditing({...editing, credit_terms_days: e.target.value})} /></Field>
                <div className="col-span-2"><Field label="Billing Address"><Input value={editing.billing_address || ""} onChange={(e) => setEditing({...editing, billing_address: e.target.value})} /></Field></div>
              </>
            )}
          </form>
        )}
      </Modal>
    </div>
  );
}
